import { useTranslation } from "react-i18next";
import { SettingsFormContent, SettingsFormCTAs, SettingsHeader } from "./index";
import { Controller, useForm, useWatch } from "react-hook-form";
import { Grid, Stack } from "@mantine/core";
import MultiSelect, { transformDataToSelectOption } from "@/components/atoms/MultiSelect";
import Heading from "@/components/atoms/Heading";
import Text from "@/components/atoms/Text";
import { zodResolver } from "@hookform/resolvers/zod";
import { AddPartnerFormInput, AddPartnerValidationSchema } from "./validation/AddPartnerValidationSchema";
import TextInput from "@/components/atoms/TextInput";
import CTA from "@/components/atoms/CTA";
import { useMutation } from "@tanstack/react-query";
import { getCompanyByOrgNumber, inviteCompany } from "@/services/inviteCompany";
import { transformCompanyData } from "@/utils/duns";
import { notifications } from "@mantine/notifications";
import React from "react";
import { useGetContractTemplates } from "@/services/Contracts";
import { useAuth } from "@/contexts/AuthContext";
import { ErrorNotificationObj, SuccessNotificationObj } from "@/utils/NotificationUtil";
import Checkbox from "@/components/atoms/Checkbox";
import axios from "axios";

export const Customers: React.FC = () => {
  const { t } = useTranslation();
  const { currentCompany } = useAuth();

  return (
    <Stack data-testid="customers-settings">
      <Heading order={2} styleOrder={4} tt="uppercase">
        {t("settings:navigation.partners")}
      </Heading>
      <AddPartnerForm companyId={currentCompany?.companyId} />
    </Stack>
  );
};

export default Customers;

const getInviteErrorNotification = (error: unknown) => {
  const response = axios.isAxiosError(error) ? error.response : undefined;
  const body = response?.data;
  const code = (typeof body === "string" ? body : JSON.stringify(body ?? "")).match(/USR-\d{3}/)?.[0];

  if (code === "USR-232" || response?.status === 409) {
    return { messageKey: "notifications:settings.companyAlreadyExists", id: "partner-already-exists-error" };
  }
  if (code === "USR-127" || response?.status === 404) {
    return { messageKey: "notifications:settings.dunsNotFound", id: "partner-duns-not-found-error" };
  }
  return { messageKey: "notifications:settings.companyInvitedError", id: "partner-invited-error" };
};

const AddPartnerForm = ({ companyId }: { companyId: number | undefined }) => {
  const { t } = useTranslation();
  const { data } = useGetContractTemplates(companyId as number, {
    enabled: !!companyId,
  });

  const {
    handleSubmit,
    control,
    formState: { isValid, errors },
    getValues,
    setValue,
    trigger,
  } = useForm<AddPartnerFormInput>({
    resolver: zodResolver(AddPartnerValidationSchema),
    mode: "onChange",
    reValidateMode: "onChange",
    values: {
      orgNumber: "",
      companyName: "",
      country: "NO",
      address: "",
      postalCode: "",
      region: "",
      city: "",
      duns: "",
      contractTemplateIds: [],
      isNorwegian: true,
      outsideEu: false,
    },
  });

  const isNorwegian = useWatch({ control, name: "isNorwegian" });
  const orgNumber = useWatch({ control, name: "orgNumber" });
  const duns = useWatch({ control, name: "duns" });

  const revalidateOrgNumber = () => {
    if (getValues("orgNumber")) void trigger("orgNumber");
  };

  const clearLookedUpCompany = () => {
    if (!getValues("duns")) return;
    setValue("companyName", "");
    setValue("country", "NO");
    setValue("address", "");
    setValue("postalCode", "");
    setValue("region", "");
    setValue("city", "");
    setValue("duns", "");
    void trigger();
  };

  const canLookUpCompany = !!orgNumber && !errors.orgNumber;

  const dunsErrorKey = !canLookUpCompany
    ? undefined
    : !duns
      ? "validation:dunsRequired"
      : !/^\d+$/.test(duns)
        ? "validation:invalidDuns"
        : undefined;

  const { mutateAsync: handleSearch, isPending } = useMutation({
    mutationFn: async (orgNumber: string) => {
      return await getCompanyByOrgNumber(orgNumber);
    },
    onSuccess: (res) => {
      const transformedData = res ? transformCompanyData(res.data) : null;
      if (transformedData) {
        setValue("companyName", transformedData.name || "");
        setValue("country", transformedData.countryCode || "");
        setValue("address", transformedData.address || "");
        setValue("postalCode", transformedData.postalCode || "");
        setValue("region", transformedData.region || "");
        setValue("city", transformedData.city || "");
        setValue("duns", transformedData.duns || "");
        void trigger();
      } else {
        notifications.show(
          ErrorNotificationObj(
            t("notifications:generic.error"),
            t("notifications:settings.companySearchError"),
            "company-search-no-results"
          )
        );
      }
    },
    onError: () => {
      notifications.show(
        ErrorNotificationObj(
          t("notifications:generic.error"),
          t("notifications:settings.companySearchError"),
          "company-search-error"
        )
      );
    },
  });

  const { mutateAsync: inviteCompanyMutation, isPending: isPendingInviteCompany } = useMutation({
    mutationFn: async (data: AddPartnerFormInput) => {
      const contractTemplateIds = data.contractTemplateIds.map((templateId) => parseInt(templateId));
      return await inviteCompany(data.duns, contractTemplateIds, data.orgNumber.replace(/\s/g, ""));
    },
    onSuccess: () => {
      notifications.show(
        SuccessNotificationObj(
          t("notifications:generic.success"),
          t("notifications:settings.companyInvited"),
          "partner-invited-success"
        )
      );
    },
    onError: (error) => {
      const { messageKey, id } = getInviteErrorNotification(error);
      notifications.show(ErrorNotificationObj(t("notifications:generic.error"), t(messageKey), id));
    },
  });

  const onSubmit = async (data: AddPartnerFormInput) => {
    await inviteCompanyMutation(data);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <SettingsFormContent>
        <Grid>
          <SettingsHeader>
            <Text size="md" strong={true} display="technical" tt="uppercase">
              {t("settings:partnersSettings.addCompany")}
            </Text>
          </SettingsHeader>

          <Grid.Col span={{ base: 12, md: 6 }}>
            <Controller
              control={control}
              name="orgNumber"
              render={({ field, fieldState }) => (
                <TextInput
                  {...field}
                  id="orgNumber"
                  data-testid="org-number-input"
                  label={t("settings:partnersSettings.organisationNumber")}
                  placeholder=""
                  value={field.value?.replace(/\s/g, "") || ""}
                  onChange={(e) => {
                    field.onChange(e);
                    clearLookedUpCompany();
                  }}
                  error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                />
              )}
            />
            <Controller
              control={control}
              name="isNorwegian"
              render={({ field }) => (
                <Checkbox
                  mt={10}
                  data-testid="norwegian-company-checkbox"
                  label={t("settings:partnersSettings.norwegianCompany")}
                  checked={field.value}
                  onChange={(e) => {
                    field.onChange(e.currentTarget.checked);
                    if (e.currentTarget.checked) setValue("outsideEu", false);
                    revalidateOrgNumber();
                  }}
                />
              )}
            />
            <Controller
              control={control}
              name="outsideEu"
              render={({ field }) => (
                <Checkbox
                  mt={10}
                  data-testid="outside-eu-checkbox"
                  disabled={isNorwegian}
                  label={t("settings:partnersSettings.outsideEu")}
                  checked={field.value}
                  onChange={(e) => {
                    field.onChange(e.currentTarget.checked);
                    revalidateOrgNumber();
                  }}
                />
              )}
            />
          </Grid.Col>
          <Grid.Col span={6}>
            <CTA
              mt={25}
              intent="secondary"
              data-testid="get-info-btn"
              onClick={() => handleSearch(getValues("orgNumber").replace(/\s/g, ""))}
              disabled={isPending || !canLookUpCompany}
            >
              {t("settings:partnersSettings.getInfo")}
            </CTA>
            {dunsErrorKey && (
              <Text mt={10} size="sm" c="var(--color-error-primary)" data-testid="duns-error">
                {t(dunsErrorKey)}
              </Text>
            )}
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Controller
              control={control}
              name="companyName"
              render={({ field, fieldState }) => (
                <TextInput
                  {...field}
                  disabled={true}
                  id="companyName"
                  label={t("settings:partnersSettings.companyName")}
                  value={field.value || ""}
                  error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                />
              )}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Controller
              control={control}
              name="country"
              render={({ field, fieldState }) => (
                <TextInput
                  {...field}
                  id="country"
                  disabled={true}
                  label={t("settings:partnersSettings.country")}
                  value={field.value || ""}
                  error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                />
              )}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Controller
              control={control}
              name="address"
              render={({ field, fieldState }) => (
                <TextInput
                  {...field}
                  id="address"
                  disabled={true}
                  label={t("settings:partnersSettings.address")}
                  value={field.value || ""}
                  error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                />
              )}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Controller
              control={control}
              name="city"
              render={({ field, fieldState }) => (
                <TextInput
                  {...field}
                  id="city"
                  disabled={true}
                  label={t("settings:partnersSettings.city")}
                  value={field.value || ""}
                  error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                />
              )}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Controller
              control={control}
              name="postalCode"
              render={({ field, fieldState }) => (
                <TextInput
                  {...field}
                  id="postalCode"
                  disabled={true}
                  label={t("settings:partnersSettings.postalCode")}
                  value={field.value || ""}
                  error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                />
              )}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Controller
              control={control}
              name="region"
              render={({ field, fieldState }) => (
                <TextInput
                  {...field}
                  id="region"
                  disabled={true}
                  label={t("settings:partnersSettings.region")}
                  value={field.value || ""}
                  error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                />
              )}
            />
          </Grid.Col>
          <Grid.Col span={12}>
            <Controller
              control={control}
              name="contractTemplateIds"
              render={({ field, fieldState }) => (
                <MultiSelect
                  label={t("settings:templates.selectTemplates")}
                  data={transformDataToSelectOption(data || [], "templateId", "name", t)}
                  error={fieldState.error?.message || undefined}
                  {...field}
                />
              )}
            />
          </Grid.Col>
        </Grid>
      </SettingsFormContent>
      <SettingsFormCTAs>
        <CTA
          type={"submit"}
          disabled={!isValid}
          id="save"
          data-testid="submit-btn"
          size="lg"
          loading={isPending || isPendingInviteCompany}
        >
          {t("actions:add")}
        </CTA>
      </SettingsFormCTAs>
    </form>
  );
};
